import React from 'react';
import { Spacing, DetailItemList, ExpandableContent } from '@doveit/bricks';
import { formatDate } from '@doveit/hammer';
import { Contact } from '../../../providers/api/dtos';
import UserActions, { UserActionsProps } from '../../../components/user-actions/UserActions';
import TextPreview from '../../../components/text-preview/TextPreview';
import { identityDocumentTypeLabels } from '../../../labels';

export interface ContactInfoProps extends React.AriaAttributes {
  contact: Contact,
  hideActions?: boolean,
  actions?: Omit<UserActionsProps, 'user'>
}

const ContactInfo: React.FC<ContactInfoProps> = ({
  contact,
  actions,
  hideActions = false,
  ...rest
}) => (
  <div {...rest}>
    <DetailItemList>
      {contact.name && (
        <DetailItemList.Item
          label="Nome"
          aria-label="Nome del contatto"
        >
          {contact.name}
        </DetailItemList.Item>
      )}
      {contact.phoneNumber && (
        <DetailItemList.Item
          label="Telefono"
          aria-label="Numero telefonico del contatto"
          textToCopy={contact.phoneNumber}
        >
          {contact.phoneNumber}
        </DetailItemList.Item>
      )}
      {contact.email && (
        <DetailItemList.Item
          label="Email"
          aria-label="Email del contatto"
          textToCopy={contact.email}
        >
          {contact.email}
        </DetailItemList.Item>
      )}
      {contact.fiscalCode && (
        <DetailItemList.Item
          label="Codice fiscale"
          aria-label="Codice fiscale del contatto"
        >
          {contact.fiscalCode}
        </DetailItemList.Item>
      )}
      {contact.residence?.normalizedAddress && (
        <DetailItemList.Item
          label="Indirizzo"
          aria-label="Indirizzo di residenza del contatto"
        >
          {contact.residence.normalizedAddress}
        </DetailItemList.Item>
      )}
      {contact.birthday && (
        <DetailItemList.Item
          label="Data di nascita"
          aria-label="Data di nascita del contatto"
        >
          {formatDate(new Date(contact.birthday))}
        </DetailItemList.Item>
      )}
      {contact.birthplace && (
        <DetailItemList.Item
          label="Luogo di nascita"
          aria-label="Luogo di nascita del contatto"
        >
          {contact.birthplace}
        </DetailItemList.Item>
      )}
      {contact.citizenship && (
        <DetailItemList.Item
          label="Nazionalità"
          aria-label="Nazionalità del contatto"
        >
          {contact.citizenship}
        </DetailItemList.Item>
      )}
      {contact.notes && (
        <DetailItemList.Item
          label="Note"
          aria-label="Note del contatto"
          inline={false}
        >
          <TextPreview text={contact.notes} />
        </DetailItemList.Item>
      )}
      {contact.identityDocument && (contact.identityDocument.type && contact.identityDocument.number) && (
        <ExpandableContent openLabel="Mostra documento">
          <DetailItemList.Group
            label="Documento di identità"
            aria-label="Dati documento di identità"
          >
            <DetailItemList.Item
              label="Tipo"
              aria-label="Tipo del documento"
            >
              {identityDocumentTypeLabels[contact.identityDocument.type]}
            </DetailItemList.Item>
            <DetailItemList.Item
              label="Numero"
              aria-label="Numero del documento"
            >
              {contact.identityDocument.number}
            </DetailItemList.Item>
            {contact.identityDocument.issuedBy && (
              <DetailItemList.Item
                label="Emittente"
                aria-label="Emittente del documento"
              >
                {contact.identityDocument.issuedBy}
              </DetailItemList.Item>
            )}
            {contact.identityDocument.issuedAt && (
              <DetailItemList.Item
                label="Data di emissione"
                aria-label="Data di emissione del documento"
              >
                {formatDate(new Date(contact.identityDocument.issuedAt))}
              </DetailItemList.Item>
            )}
          </DetailItemList.Group>
        </ExpandableContent>
      )}
    </DetailItemList>
    {
      !hideActions && (
        <Spacing margin={[200, 0, 0]}>
          <UserActions
            {...actions}
            user={contact}
          />
        </Spacing>
      )
    }
  </div>
);

export default ContactInfo;
